import Tooltip from '@material-ui/core/Tooltip';
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import PieChart from '@components/PieChart';
import { toastError } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { _getKey, changeDateFormat, convertNumberToUSFormat, getNumberUnit } from '@utils/Util';

const getLastJanuary = () => {
	const currentMonth = new Date().getMonth() + 1;
	const requiredMonth = currentMonth + 11;
	return moment().subtract(requiredMonth, 'months').startOf('month').format('MM/DD/YYYY');
};

const BarChart = () => {
	const [businessData, setBusinessData] = useState([]);
	const [, setIsOpenDropdown] = useState(false);
	const [sortType, setSortType] = useState({
		startDate: getLastJanuary(),
		endDate: moment().format('MM/DD/YYYY')
	});
	const [maxValue, setMaxValue] = useState(null);
	const [rangeValues, setRangeValues] = useState([]);
	const [orderSummary, setOrderSummary] = useState({});
	const dateRangeRef = useRef(false);
	const dispatch = useDispatch();

	useEffect(() => {
		getBusinessOverView(sortType?.startDate, sortType?.endDate);
	}, [sortType]);

	const onSetMax = (item) => Math.ceil(Math.max(...item.map((o) => o.value)) / 1000000) * 1000000;

	const calculateRanges = (value) => {
		const percentages = [1, 0.75, 0.5, 0.25, 0];
		return percentages.map((p) => getNumberUnit(value * p));
	};

	const onSetRangeValues = (value) => {
		const ranges = calculateRanges(value);
		setRangeValues(ranges);
	};

	const fetchOrderSummary = async (params = '') => {
		await ApiClient.GET('getOrderDashboardSummary', params)
			.then((response) => {
				setOrderSummary(response.data);
			})
			.catch((error) => {
				toastError(error.response.data.message);
			});
	};

	const getBusinessOverView = async (startDate, endDate) => {
		dispatch(openLoader());
		if (startDate && endDate) {
			const body = {
				fromDate: changeDateFormat(startDate, '', 'YYYY-MM-DD'),
				toDate: changeDateFormat(endDate, '', 'YYYY-MM-DD')
			};
			await fetchOrderSummary(`?poReceivedDateFrom=${body.fromDate}&poReceivedDateTo=${body.toDate}`);
			await ApiClient.GET('getBusinessOverView', body)
				.then(({ data }) => {
					dispatch(closeLoader());
					if (data) {
						setBusinessData(data);
						const processedMaxValue = onSetMax(data?.monthWiseBusinessOverviewList);
						setMaxValue(processedMaxValue);
						onSetRangeValues(processedMaxValue);
					}
				})
				.catch(({ response }) => {
					dispatch(closeLoader());
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError('Something went wrong! Please try again.');
					}
				});
		} else {
			dispatch(closeLoader());
		}
	};

	const onChangeMonthRange = (e) => {
		setIsOpenDropdown(false);
		if (e.startDate && e.endDate) {
			setSortType({
				startDate: e.startDate,
				endDate: e.endDate
			});
		}
	};

	const onSortCLick = (openModal = false) => {
		if (!openModal) {
			setIsOpenDropdown(false);
		}
		setSortType({
			startDate: moment().subtract(1, 'years').format('MM/DD/YYYY'),
			endDate: moment().format('MM/DD/YYYY')
		});
	};
	const getParcentage = (value) => {
		return (value * 100) / maxValue;
	};

	const getPercentageValue = (n1 = 0, n2 = 0) => {
		return (n1 / (n1 + n2)) * 100;
	};

	const generateChartData = () => {
		const nitexCount = getPercentageValue(orderSummary.nitexDesignCount, orderSummary.buyerDesignCount);
		const buyerCount = getPercentageValue(orderSummary.buyerDesignCount, orderSummary.nitexDesignCount);
		return {
			labels: ['', ''],
			datasets: [
				{
					label: '',
					data: [nitexCount, buyerCount],
					backgroundColor: ['rgba(59, 130, 246, 1)', 'rgba(171, 213, 117, 1)'],
					borderWidth: 0
				}
			]
		};
	};

	return (
		<div>
			<section className="dashboard-overview-section">
				<div className="dashboard-month-wise-breakpoints common-style">
					<div className="d-flex justify-content-between mb-4 title-and-date">
						<div>
							<h3 className="semibold-18">Monthly order booking</h3>
							<p className="regular-14 gray_dark_02">
								{changeDateFormat(sortType?.startDate, '', 'MMM, YYYY')} -{' '}
								{changeDateFormat(sortType?.endDate, '', 'MMM, YYYY')}
							</p>
						</div>
						<div className="d-flex align-items-center">
							{/* <img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="" className="mr-1" /> */}
							<DateRangePickerComponent
								cssClass="e-selected"
								open={() => (dateRangeRef.current = true)}
								close={() => (dateRangeRef.current = false)}
								blur={() => setIsOpenDropdown(false)}
								format="MMM, yyyy"
								start={'Year'}
								depth={'Year'}
								placeholder="Choose a date range"
								change={onChangeMonthRange}
								cleared={() => onSortCLick(true)}
								value={{
									start: sortType?.startDate,
									end: sortType?.endDate
								}}
								openOnFocus={true}
								strictMode={true}
								showClearButton={true}
							/>
						</div>
					</div>
					<div className="commercial-dashboard-container sales-performance-chart">
						<div className="date-wise-summery w-100">
							<div className="breakpoints">
								{rangeValues?.map((item) => (
									<p className="regular-10 gray_dark_02 mb-12" key={_getKey()}>
										{item !== 0 && item}
									</p>
								))}
							</div>
							<div className="all-progress-summary scroll-x-label">
								{businessData?.monthWiseBusinessOverviewList?.map((item) => (
									<div className="single-bar cursor-pointer" key={item.month}>
										<Tooltip
											title={
												<p className="regular-14">${getNumberUnit(item?.value)}</p>
											}
											placement="top"
											arrow
										>
											<div className="shipment-and-order-progress">
												<div className="shipment-progress">
													<div
														className="progress-count"
														style={{
															height: getParcentage(item?.value) + '%'
														}}
													/>
												</div>
											</div>
										</Tooltip>
										<span className="status-date">
											{changeDateFormat(item?.startDate, '', 'MMM, YY')}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
				<div className="dashboard-month-wise-summary">
					<div className="value-and-units w-100 d-flex justify-content-between">
						<div className="single-one common-style">
							<h3 className="value mb-1">
								{orderSummary?.poAmount > 0
									? `$${getNumberUnit(orderSummary?.poAmount)}`
									: '0'}
							</h3>
							<p className="regular-14 gray_dark_02 value-label">Order Value</p>
						</div>
						<div className="single-one common-style ml-3">
							<h3 className="value mb-1">
								{orderSummary?.poQuantity > 0
									? `${getNumberUnit(orderSummary?.poQuantity)}`
									: '0'}
							</h3>
							<p className="regular-14 gray_dark_02 value-label">Units </p>
						</div>
					</div>
					<div className="designs-summary common-style">
						<div className="single-summary">
							<div className="chart">
								<PieChart data={generateChartData()} />
							</div>
							<div className="ml-2">
								<p className="regular-18 gray_dark_02">
									<span className="c-indicator nitex" />
									Nitex design
									<span className="semibold-18 ml-1">
										{orderSummary?.nitexDesignCount > 0
											? `${convertNumberToUSFormat(
													orderSummary?.nitexDesignCount,
													true
												)}`
											: 0}
										{orderSummary.nitexDesignCount > 0
											? `(${getPercentageValue(
													orderSummary.nitexDesignCount,
													orderSummary.buyerDesignCount
												).toFixed(2)}%)`
											: '(0%)'}
									</span>
								</p>
								<p className="regular-18 gray_dark_02">
									<span className="c-indicator buyer" />
									Buyer design
									<span className="semibold-18 ml-1">
										{orderSummary?.buyerDesignCount > 0
											? `${convertNumberToUSFormat(
													orderSummary?.buyerDesignCount,
													true
												)} `
											: '0'}
										{orderSummary?.buyerDesignCount > 0
											? `(${getPercentageValue(
													orderSummary.buyerDesignCount,
													orderSummary.nitexDesignCount
												).toFixed(2)}%)`
											: '(0%)'}
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
};
export default BarChart;
